import { ElectionType, electionTypeMap } from '../../api/api-types';
import { thousandsSeparators } from '../../utils/format-utils';
import type { ElectionBadge, PartyListParty } from './partyListModel';

type Translate = (key: string, values?: Record<string, unknown>) => string;

export function formatExpiryDate(date?: string, fallback?: string) {
  return date
    ? new Date(date).toLocaleDateString('da-DK', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    : fallback;
}

export function getPartyHref(
  party: Pick<PartyListParty, 'id' | 'type'>,
  partyDetailsUrl: string,
) {
  return `${partyDetailsUrl}?election=${
    party.type === ElectionType.BothTypes
      ? electionTypeMap[ElectionType.Folketingsvalg]
      : electionTypeMap[party.type || ElectionType.Folketingsvalg]
  }&party=${party.id}`;
}

export function getElectionBadges(
  type?: ElectionType,
  t?: Translate,
): ElectionBadge[] {
  if (type === ElectionType.BothTypes) {
    return [
      { label: 'FT', title: t?.('dkElection') || 'Folketingsvalg' },
      { label: 'EU', title: t?.('euElection') || 'Europa-parlamentsvalg' },
    ];
  }

  if (type === ElectionType.EuropaParlamentsvalg) {
    return [
      { label: 'EU', title: t?.('euElection') || 'Europa-parlamentsvalg' },
    ];
  }

  return [{ label: 'FT', title: t?.('dkElection') || 'Folketingsvalg' }];
}

export function getPartyVoteParts(party: PartyListParty, t: Translate) {
  const folketingLabel = t('dkElection') || 'Folketingsvalg';
  const europeanLabel = t('euElection') || 'Europa-parlamentsvalg';

  if (party.type === ElectionType.BothTypes) {
    return [
      { label: folketingLabel, value: party.voteCount ?? 0 },
      { label: europeanLabel, value: party.voteCount2 ?? 0 },
    ];
  }

  return [
    {
      label:
        party.type === ElectionType.EuropaParlamentsvalg
          ? europeanLabel
          : folketingLabel,
      value: party.voteCount ?? 0,
    },
  ];
}

export function getPartyExpiryDateParts(party: PartyListParty, t: Translate) {
  const folketingLabel = t('dkElection') || 'Folketingsvalg';
  const europeanLabel = t('euElection') || 'Europa-parlamentsvalg';

  if (party.type === ElectionType.BothTypes) {
    return [
      { label: folketingLabel, value: formatExpiryDate(party.expiryDate) },
      { label: europeanLabel, value: formatExpiryDate(party.expiryDate2) },
    ];
  }

  return [
    {
      label:
        party.type === ElectionType.EuropaParlamentsvalg
          ? europeanLabel
          : folketingLabel,
      value: formatExpiryDate(party.expiryDate),
    },
  ];
}

export function getVisibleExpiryDates(party: PartyListParty) {
  return party.type === ElectionType.BothTypes
    ? [
        formatExpiryDate(party.expiryDate, '-'),
        formatExpiryDate(party.expiryDate2, '-'),
      ]
    : [formatExpiryDate(party.expiryDate, '-')];
}

export function getPartyVoteAriaLabel(party: PartyListParty, t: Translate) {
  return getPartyVoteParts(party, t)
    .map(
      (part) =>
        `${part.label}: ${thousandsSeparators(part.value)} vælgererklæringer`,
    )
    .join(', ');
}

export function getPartyExpiryDateAriaLabel(
  party: PartyListParty,
  t: Translate,
) {
  return getPartyExpiryDateParts(party, t)
    .map(
      (part) =>
        `${part.label} udløbsdato: ${part.value || 'ikke oplyst'}`,
    )
    .join(', ');
}

export function getPartyLinkAriaLabel(party: PartyListParty, t: Translate) {
  return [
    party.name,
    getPartyVoteAriaLabel(party, t),
    getPartyExpiryDateAriaLabel(party, t),
    `${t('parties.partiesAlsoEuroparliment')}: ${getElectionBadges(
      party.type,
      t,
    )
      .map((badge) => badge.title)
      .join(', ')}`,
  ]
    .filter(Boolean)
    .join('. ');
}
